import { CopyCheck, Download, PencilIcon, Star, Trash2 } from 'lucide-react';
import { Board } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import { updateBoard } from 'modules/template-editor/store/templates/actions';
import { favoriteBoard, setActiveBoard } from 'modules/template-editor/store/templates/templates';
import { increaseClickArea } from 'modules/template-editor/ui/pages/styles';
import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import useDeleteModal from 'modules/template-editor/ui/hooks/modals/board/delete';
import useCopyModal from 'modules/template-editor/ui/hooks/modals/board/duplicate';
import useEditModal from 'modules/template-editor/ui/hooks/modals/board/edit';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import EmptyButton from 'ud-ui/components/empty-button';
import DownIndicatorIcon from 'ud-ui/components/icon/down-indicator-icon';
import Portal from 'ud-ui/components/portal/portal';
import UDText from 'ud-ui/components/text';
import * as S from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  board: Board;
  isEditable: boolean;
  onClick: () => void;
  isActive?: boolean;
  isDefaultTemplate?: boolean;
  additionalButton?: React.ReactNode;
  icon?: React.ReactNode;
}

function BoardItem(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { board, isEditable, onClick, isActive, isDefaultTemplate = false, additionalButton, icon } = props;
  const { modal: renameModal, open: openRenameModal } = useEditModal();
  const { modal: deleteModal, open: openDeleteModal } = useDeleteModal();
  const { modal: copyModal, open: openCopyModal } = useCopyModal();
  const [isDropDown, setIsDropDown] = useState(false);
  const [modalStyles, setModalStyles] = useState<CSSProperties>({});
  const { boards } = useBoards();

  const isLast = boards.length === 1;

  const onEditClick = () => {
    dispatch(setActiveBoard({ id: board.id }));
    openRenameModal();
  };

  const onDeleteClick = () => {
    dispatch(setActiveBoard({ id: board.id }));
    openDeleteModal();
  };

  const onCopyClick = useCallback(() => {
    dispatch(setActiveBoard({ id: board.id }));
    openCopyModal();
  }, [board.id, dispatch, openCopyModal]);

  const onFavoriteClick = useCallback(() => {
    dispatchWithToast(updateBoard({ id: board.id, isFavorite: !board.isFavorite }), { error: 'Не удалось обновить табло' });
    dispatch(favoriteBoard({ id: board.id }));
  }, [board.id, board.isFavorite, dispatch, dispatchWithToast]);

  const favoriteIsAvailable = useMemo(() => {
    if (isDefaultTemplate) return false;
    let count = 0;
    for (const b of boards) {
      b.isFavorite && count++;
    }
    return count < 3;
  }, [boards, isDefaultTemplate]);

  const isEvents = useMemo(() => {
    return board.type === 'events';
  }, [board.type]);

  useEffect(() => {
    if (!isActive) {
      setIsDropDown(false);
    }
  }, [isActive]);

  const onClickIndicator = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const target = event.target as HTMLElement;
      const parentBounding = target.parentElement?.getBoundingClientRect();
      if (parentBounding) {
        setModalStyles({
          position: 'absolute',
          top: parentBounding?.top,
          left: parentBounding?.left,
          width: parentBounding?.width,
        });
      }

      if (isDropDown) {
      }
      setIsDropDown((prevState) => !prevState);
    },
    [isDropDown],
  );

  useEffect(() => {
    const onScroll = () => {
      setIsDropDown(false);
    };

    const onClick = (event: MouseEvent) => {
      const clickOnBoardList = (event.target as HTMLElement)?.closest('#boardsList');
      const clickOnModal = (event.target as HTMLElement)?.closest('#modal');
      if (!clickOnBoardList && !clickOnModal) {
        setIsDropDown(false);
      }
    };

    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropDown(false);
      }
    };

    window.addEventListener('scrollBoardsList', onScroll);
    document.addEventListener('click', onClick);
    window.addEventListener('keydown', onEsc);

    return () => {
      window.removeEventListener('scrollBoardsList', onScroll);
      document.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onEsc);
    };
  }, []);

  const exportBoard = useCallback(() => {
    const link = document.createElement('a');
    const { id, templateId, alias, createdAt, isFavorite, ...restProperty } = board;
    link.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(JSON.stringify({ ...restProperty }));
    link.download = `${board.name}.board`;
    link.click();
    link.remove();
  }, [board]);

  const dropDownItems = useMemo(() => {
    return items.map((item) => {
      let isEnable = true;
      let onClick = () => {};

      switch (item.id) {
        case 'favorite': {
          isEnable = (favoriteIsAvailable || board.isFavorite) && !isEvents;
          onClick = onFavoriteClick;
          break;
        }
        case 'delete': {
          isEnable = !isLast;
          onClick = onDeleteClick;
          break;
        }
        case 'edit': {
          onClick = onEditClick;
          break;
        }
        case 'duplicate': {
          onClick = onCopyClick;
          break;
        }
        case 'export': {
          onClick = exportBoard;
          break;
        }
      }

      return (
        <S.DropDownItem key={item.id + board.id}>
          <EmptyButton
            onClick={() => {
              onClick();
              setIsDropDown(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
            color="white"
            icon={
              <div className={'flex-center'} style={{ width: 24 }}>
                {item?.icon}
              </div>
            }
            disabled={!isEnable}
          >
            {item.text}
          </EmptyButton>
        </S.DropDownItem>
      );
    });
  }, [
    board.id,
    board.isFavorite,
    exportBoard,
    favoriteIsAvailable,
    isEvents,
    isLast,
    onCopyClick,
    onDeleteClick,
    onEditClick,
    onFavoriteClick,
  ]);

  return (
    <>
      <S.BoardItem
        onClick={onClick}
        key={board.id}
        className={isDropDown ? 'squareBoard' : ''}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: isActive ? theme.colors.WhiteGray : 'transparent',
          color: isActive ? theme.colors.SurfaceWhite : theme.colors.SurfaceRaisinBlack50,
        }}
      >
        <div className={'flex'} style={{ color: 'inherit', gap: 10, minWidth: 0 }}>
          {icon}
          {board.isFavorite && <Star size={20} style={{ minWidth: 20, minHeight: 20 }} color={theme.colors.AccentNaplesYellow} />}
          <UDText style={{ color: 'inherit' }} type={'subhead'}>
            {board.name}
          </UDText>
        </div>
        {isEditable && (
          <EmptyButton onClick={onClickIndicator} css={increaseClickArea({ height: 40, width: 40 })} style={{ flexShrink: 0 }}>
            <DownIndicatorIcon
              width={20}
              height={20}
              color={isActive ? theme.colors.AccentNaplesYellow : theme.colors.SurfacePlatinum20}
              transform={isDropDown ? 'rotate(180)' : 'rotate(0)'}
            />
          </EmptyButton>
        )}
        {additionalButton}
        <Portal wrapperId={board.name}>
          <S.Modal id={'modal'} style={modalStyles} className={isDropDown ? 'show' : ''} isOpen={true}>
            <S.BoardItem
              key={'boardItem-' + board.id}
              className={isDropDown ? 'squareBoard' : ''}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: isActive ? theme.colors.WhiteGray : 'transparent',
                color: isActive ? theme.colors.SurfaceWhite : theme.colors.SurfaceRaisinBlack50,
                cursor: 'default',
              }}
            >
              <div className={'flex'} style={{ color: 'inherit', gap: 10, minWidth: 0 }}>
                {icon}
                {board.isFavorite && (
                  <Star size={20} style={{ minWidth: 20, minHeight: 20 }} color={theme.colors.AccentNaplesYellow} />
                )}
                <UDText style={{ color: 'inherit' }} type={'subhead'}>
                  {board.name}
                </UDText>
              </div>
              <EmptyButton onClick={onClickIndicator} css={increaseClickArea({ height: 40, width: 40 })} style={{ flexShrink: 0 }}>
                <DownIndicatorIcon
                  width={20}
                  height={20}
                  color={isActive ? theme.colors.AccentNaplesYellow : theme.colors.SurfacePlatinum20}
                  transform={isDropDown ? 'rotate(180)' : 'rotate(0)'}
                />
              </EmptyButton>
            </S.BoardItem>
            <S.DropDownMenu className={isDropDown ? 'show' : ''}>{dropDownItems}</S.DropDownMenu>
          </S.Modal>
        </Portal>
        {renameModal}
        {deleteModal}
        {copyModal}
      </S.BoardItem>
    </>
  );
}

export default BoardItem;

const items = [
  {
    id: 'edit',
    text: 'Редактировать',
    icon: <PencilIcon size={20} />,
  },
  {
    id: 'favorite',
    text: 'Избранное',
    icon: <Star size={20} />,
  },
  {
    id: 'duplicate',
    text: 'Дублировать',
    icon: <CopyCheck size={20} />,
  },
  {
    id: 'export',
    text: 'Экспортировать',
    icon: <Download size={20} />,
  },
  {
    id: 'delete',
    text: 'Удалить',
    icon: <Trash2 size={20} />,
  },
];
